import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, PageHeader } from '@/shared/ui';
import { useToast } from '@/shared/ui/Toast';
import { useAuth } from '@/shared/hooks/useAuth';
import { CommsPreferenceCard } from '@/modules/agents/components/comms/CommsPreferenceCard';
import type { CommunicationPreferencePatch } from '@/modules/agents/utils/commsLabels';
import { practiceCommsApi } from '../api/commsApi';
import { settingsKeys } from '../queryKeys';
import styles from './SettingsPage.module.css';

function errorMessage(err: unknown): string {
  const message = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data
    ?.error?.message;
  return message ?? 'Could not save — try again.';
}

/**
 * Settings → Notifications: how each of the practice's agents reaches it. One card per agent that is
 * switched on for this practice, editing the same record the agency sees in its Command Center.
 */
export function NotificationsPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'owner';

  const prefs = useQuery({
    queryKey: settingsKeys.comms(),
    queryFn: () => practiceCommsApi.list(),
  });

  const save = useMutation({
    mutationFn: (args: { agentType: string; patch: CommunicationPreferencePatch }) =>
      practiceCommsApi.update(args.agentType, args.patch),
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.comms() });
      toast.success(`${saved.personaName}'s preferences saved.`);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <div className={styles.page}>
      <PageHeader
        title="Notifications"
        subtitle="How each of your agents keeps you in the loop: where they reach you, how often, and what always comes through right away."
      />
      {prefs.isLoading && <p className={styles.loadingState}>Loading…</p>}
      {prefs.isError && (
        <Card>
          <p className={styles.emptyState}>Your notification preferences could not load. Try again in a moment.</p>
        </Card>
      )}
      {prefs.data && prefs.data.length === 0 && (
        <Card>
          <p className={styles.emptyState}>
            No agents are working for your practice yet. Preferences appear here once one is switched on.
          </p>
        </Card>
      )}
      {prefs.data?.map((pref) => (
        <CommsPreferenceCard
          key={pref.agentType}
          preference={pref}
          canEdit={canEdit}
          saving={save.isPending && save.variables?.agentType === pref.agentType}
          onSave={(patch) => save.mutate({ agentType: pref.agentType, patch })}
        />
      ))}
    </div>
  );
}
